"use client";

import { useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Bornes du curseur de largeur (en pixels).
const MIN_WIDTH = 320;
const MAX_WIDTH = 1100;
const DEFAULT_WIDTH = 480;

// Seuils de breakpoints, calculés EN JS à partir du curseur (pas via md:/lg:
// qui réagiraient au viewport réel, pas au simulateur).
const MD_BREAKPOINT = 768;
const LG_BREAKPOINT = 1024;

// Détermine le palier courant à partir de la largeur simulée.
function getBreakpoint(width) {
    if (width >= LG_BREAKPOINT) return "lg";
    if (width >= MD_BREAKPOINT) return "md";
    return "mobile";
}

// Lecture pédagogique des classes : chaque ligne montre une utilitaire de base
// face à sa variante préfixée. `active` est piloté par l'état local.
// IMPORTANT : toutes les classes Tailwind potentielles apparaissent ici comme
// chaînes littérales (jamais concaténées) pour que le compilateur les génère.
const CLASS_ROWS = [
    {
        base: { label: "flex-col", activeWhen: "mobile" },
        variant: { label: "md:flex-row", activeWhen: "md+" },
    },
    {
        base: { label: "text-sm", activeWhen: "mobile" },
        variant: { label: "lg:text-lg", activeWhen: "lg" },
    },
    {
        base: { label: "hidden", activeWhen: "belowLg" },
        variant: { label: "lg:block", activeWhen: "lg" },
    },
    {
        base: { label: "bg-white", activeWhen: "light" },
        variant: { label: "dark:bg-grayDark-900", activeWhen: "dark" },
    },
];

// Styles d'une pastille de classe selon qu'elle est active ou non.
// (Deux chaînes littérales complètes : aucune construction dynamique.)
const ACTIVE_CHIP =
    "text-sky-300 bg-sky-400/10 ring-1 ring-sky-400/30";
const INACTIVE_CHIP =
    "text-gray-500 bg-white/[0.02] ring-1 ring-white/10";

export default function TailwindResponsiveDark() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [width, setWidth] = useState(DEFAULT_WIDTH);
    const [darkOn, setDarkOn] = useState(false);

    const breakpoint = getBreakpoint(width);
    const isMd = width >= MD_BREAKPOINT;
    const isLg = width >= LG_BREAKPOINT;

    // Entrée animée (désactivée si l'utilisateur préfère moins d'animations).
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-80px" });

    // Libellé + couleur du palier courant.
    const breakpointLabel =
        breakpoint === "lg"
            ? t("Tailwind_rd_breakpoint_desktop")
            : breakpoint === "md"
              ? t("Tailwind_rd_breakpoint_tablet")
              : t("Tailwind_rd_breakpoint_mobile");

    // Détermine si une pastille de classe est active dans l'état courant.
    function isClassActive(activeWhen) {
        switch (activeWhen) {
            case "mobile":
                return breakpoint === "mobile";
            case "md+":
                return isMd;
            case "lg":
                return isLg;
            case "belowLg":
                return !isLg;
            case "light":
                return !darkOn;
            case "dark":
                return darkOn;
            default:
                return false;
        }
    }

    // Classes EXPLICITES de l'aperçu (jamais de dark:/md: réels : on pilote tout
    // depuis l'état local pour ne pas dépendre du thème global ni du viewport).
    const surface = darkOn
        ? "bg-slate-900 text-white"
        : "bg-white text-slate-900";
    const mutedText = darkOn ? "text-slate-400" : "text-slate-500";
    const cardSurface = darkOn
        ? "bg-slate-800 ring-slate-700"
        : "bg-slate-50 ring-slate-200";
    const imageSurface = darkOn ? "bg-slate-700" : "bg-slate-200";
    const imageIconColor = darkOn ? "text-slate-500" : "text-slate-400";
    const asideSurface = darkOn
        ? "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30"
        : "bg-emerald-50 text-emerald-700 ring-emerald-200";

    // Disposition de la carte calculée en JS selon le palier.
    const cardDirection = isMd ? "flex-row items-center" : "flex-col";
    const imageBox = isMd ? "w-36 h-36 shrink-0" : "w-full h-40";
    const titleSize = isLg ? "text-2xl" : isMd ? "text-xl" : "text-base";
    const textSize = isLg ? "text-base" : "text-sm";

    return (
        <section id="tailwind-responsive-dark" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Tailwind_rd_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Tailwind_rd_intro")}
            </p>

            <motion.div
                ref={ref}
                initial={reduce ? false : { opacity: 0, y: 24 }}
                animate={
                    reduce || inView ? { opacity: 1, y: 0 } : undefined
                }
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10"
            >
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 text-[11px] font-mono text-gray-400">
                        preview.html
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Tailwind_rd_badge")}
                        </span>
                    </span>
                </div>

                {/* Contenu principal */}
                <div className="relative p-5 sm:p-7">
                    {/* Contrôles : thème + largeur */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mb-6">
                        {/* Bascule clair / sombre */}
                        <div className="flex items-center gap-3">
                            <span className="text-[10px] uppercase tracking-[0.16em] text-gray-500">
                                {t("Tailwind_rd_dark_toggle_label")}
                            </span>
                            <button
                                type="button"
                                onClick={() => setDarkOn((v) => !v)}
                                aria-pressed={darkOn}
                                className="inline-flex items-center gap-2 rounded-full bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-white/10 px-3 py-1.5 text-sm font-medium text-gray-200 transition active:scale-95"
                            >
                                <SolarIcon
                                    name={darkOn ? "moon-bold" : "sun-2-bold"}
                                    className={`w-[18px] h-[18px] ${
                                        darkOn
                                            ? "text-indigo-300"
                                            : "text-amber-300"
                                    }`}
                                />
                                {darkOn
                                    ? t("Tailwind_rd_dark_label")
                                    : t("Tailwind_rd_light_label")}
                            </button>
                        </div>

                        {/* Curseur de largeur */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[10px] uppercase tracking-[0.16em] text-gray-500">
                                    {t("Tailwind_rd_width_label")}
                                </span>
                                <span className="inline-flex items-center gap-2 font-mono text-xs">
                                    <span className="rounded bg-sky-400/10 ring-1 ring-sky-400/30 px-2 py-0.5 text-sky-300 font-semibold tabular-nums">
                                        {width}px
                                    </span>
                                    <span className="text-emerald-300 font-semibold">
                                        {breakpointLabel}
                                    </span>
                                </span>
                            </div>
                            <input
                                type="range"
                                min={MIN_WIDTH}
                                max={MAX_WIDTH}
                                value={width}
                                onChange={(e) =>
                                    setWidth(Number(e.target.value))
                                }
                                aria-label={t("Tailwind_rd_width_label")}
                                className="w-full accent-sky-400 cursor-pointer"
                            />
                        </div>
                    </div>

                    <div className="grid xl:grid-cols-[1fr_280px] gap-6">
                        {/* Cadre d'appareil (largeur = valeur du curseur) */}
                        <div className="min-w-0 overflow-x-auto">
                            <div
                                style={{ width: width }}
                                className="max-w-full mx-auto rounded-xl ring-1 ring-white/15 overflow-hidden transition-[width] duration-150 ease-out"
                            >
                                <div className={`p-5 sm:p-6 ${surface}`}>
                                    <div
                                        className={`flex gap-5 ${cardDirection}`}
                                    >
                                        {/* Visuel produit */}
                                        <div
                                            className={`rounded-xl flex items-center justify-center ${imageBox} ${imageSurface}`}
                                        >
                                            <SolarIcon
                                                name="palette-bold-duotone"
                                                className={`w-10 h-10 ${imageIconColor}`}
                                            />
                                        </div>

                                        {/* Texte + action */}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-start gap-2">
                                                <h4
                                                    className={`font-bold leading-tight ${titleSize}`}
                                                >
                                                    {t(
                                                        "Tailwind_rd_card_title",
                                                    )}
                                                </h4>
                                                {/* Aside révélé uniquement en lg */}
                                                {isLg && (
                                                    <span
                                                        className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${asideSurface}`}
                                                    >
                                                        <SolarIcon
                                                            name="magic-stick-3-bold"
                                                            className="w-3 h-3"
                                                        />
                                                        lg
                                                    </span>
                                                )}
                                            </div>
                                            <p
                                                className={`mt-2 leading-relaxed ${textSize} ${mutedText}`}
                                            >
                                                {t("Tailwind_rd_card_text")}
                                            </p>
                                            <button
                                                type="button"
                                                className="mt-4 inline-flex items-center justify-center rounded-lg bg-sky-500 hover:bg-sky-400 px-4 py-2 text-sm font-semibold text-white transition active:scale-95"
                                            >
                                                {t("Tailwind_rd_card_button")}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Lecture des classes appliquées */}
                        <div className="min-w-0 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-4">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-gray-500 mb-3">
                                {t("Tailwind_rd_classes_label")}
                            </div>
                            <div className="flex flex-col gap-2.5 font-mono text-xs">
                                {CLASS_ROWS.map((row) => (
                                    <div
                                        key={row.base.label}
                                        className="flex items-center gap-2 overflow-x-auto"
                                    >
                                        <span
                                            className={`whitespace-nowrap rounded px-2 py-1 transition-colors duration-200 ${
                                                isClassActive(
                                                    row.base.activeWhen,
                                                )
                                                    ? ACTIVE_CHIP
                                                    : INACTIVE_CHIP
                                            }`}
                                        >
                                            {row.base.label}
                                        </span>
                                        <span className="text-gray-600 shrink-0">
                                            /
                                        </span>
                                        <span
                                            className={`whitespace-nowrap rounded px-2 py-1 transition-colors duration-200 ${
                                                isClassActive(
                                                    row.variant.activeWhen,
                                                )
                                                    ? ACTIVE_CHIP
                                                    : INACTIVE_CHIP
                                            }`}
                                        >
                                            {row.variant.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Tailwind_rd_caption")}
            </p>
        </section>
    );
}
